import { IkeDraw } from '@ikegps/core-shared';
import * as IkeModel from '@ikegps/core-shared';
import { IkeUnit } from '@ikegps/core-shared';
import { FieldActionButton } from '../../../components/button/field.action.button';
import { VerticalSeparationInputState } from '../../../components/input/vertical.separation.input.state';
import { NotificationService } from '../../../components/notification.toast/notification.service';
import { addStyles } from '../../../components/theme';
import { T } from '../../../i18n';
import { m } from '../../../mithril';
import type { MithrilVNode } from '../../../mithril';
import { Services } from '../../../services';
import { CurrentState } from '../../../state/page.state';
import { CaptureThumbnail } from '../../capture.thumbnail/capture.thumbnail';
import { CollectionEditCtrl } from '../collection.view.ctrl';
import type { CollectionFieldView } from './field.view';

export const CSS = {
	container: 'c-PMLink',
	isHovered: 'c-PMLink--hovered',
	isLinked: 'c-PMLink--linked',
	height: 'c-PMLink__Height',
};

export const Styles = {
	[CSS.container]: {
		display: 'flex',
		'justify-content': 'space-between',
	},
	[CSS.isHovered]: {
		'font-weight': 'bold',
	},
	[CSS.isLinked]: {
		cursor: 'pointer',
	},
	[CSS.height]: {
		width: '100%',
	},
};

addStyles(Styles);

function selectDrawable(draw: IkeDraw, drawableId: string) {
	draw.state.setSelected(drawableId, null);
	return false;
}

function hoverDrawable(captureId: string, drawableId: string, hovered: boolean) {
	const draw = CurrentState.CollectionEdit.getIkeDraw(captureId);
	if (draw == null) {
		return;
	}
	if (hovered) {
		draw.state.setHovered(drawableId);
	} else if (draw.state.hoverId === drawableId) {
		draw.state.setHovered(null);
	}
}

export function canUpdate() {
	return true;
}

export const VerticalSeparationFieldView: CollectionFieldView<IkeModel.CollectionModelFieldVerticalSeparation> = {
	viewInline(field: IkeModel.CollectionModelFieldVerticalSeparation): string {
		return IkeUnit.display(
			field.value,
			Services.UserSettings.units === 'meters' ? IkeUnit.CENTIMETER : IkeUnit.INCH,
			null,
			null,
			0
		);
	},

	hasDetail(field: IkeModel.CollectionModelFieldVerticalSeparation) {
		return field.getCaptures().length > 0;
	},

	viewDetail(field: IkeModel.CollectionModelFieldVerticalSeparation): MithrilVNode {
		if (!VerticalSeparationFieldView.hasDetail(field)) {
			return null;
		}

		const capture = field.getCaptures()[0];
		const title = Services.DateTime.formatDateTime(capture.collectedAt.iso);
		return CaptureThumbnail.view(field.collection, capture, { crosshair: true, title });
	},

	viewEdit(field: IkeModel.CollectionModelFieldVerticalSeparation, ctrl: CollectionEditCtrl): string | MithrilVNode {
		if (!canUpdate()) {
			return VerticalSeparationFieldView.viewInline(field, ctrl);
		}

		const currentDraw = CurrentState.CollectionEdit.currentDraw;
		const canLinkTo = currentDraw != null && currentDraw.drawState.hasUserMeasurements();

		let inputState = ctrl.changeState.getState(field.id) as VerticalSeparationInputState;
		if (inputState == null) {
			inputState = new VerticalSeparationInputState(field.schema.displayName);
			ctrl.changeState.addState(field.id, inputState);
		}

		inputState.validateInput(field.stateValue);

		if (field.hasValue()) {
			let className = CSS.container;
			const heightInline = VerticalSeparationFieldView.viewInline(field, ctrl);

			if (currentDraw != null) {
				const stateValue = field.stateValue;
				const drawableTopId = field.stateValue.drawableTop;
				const drawableBottomId = field.stateValue.drawableBottom;
				// If the current field is hovered
				if (currentDraw.state.hoverId === drawableTopId || currentDraw.state.hoverId === drawableBottomId) {
					className = className + ' ' + CSS.isHovered;
				}
				// If the current field is part of the currentDrawable allow it to select the drawable
				let onclick = null;
				if (stateValue.captureId === currentDraw.capture.objectId) {
					className = className + ' ' + CSS.isLinked;
					onclick = () => selectDrawable(currentDraw, drawableTopId);
				}

				// Set the hoverId for the drawable
				const onmouseover = () => hoverDrawable(stateValue.captureId, drawableTopId, true);
				const onmouseout = () => hoverDrawable(stateValue.captureId, drawableTopId, false);

				return m('div', { className, onmouseover, onmouseout }, [
					m('span', { className: CSS.height, onclick }, heightInline),
				]);
			}

			return m('div', { className }, heightInline);
		}

		if (!canLinkTo) {
			return this.viewInline(field, ctrl);
		}

		const linkFieldIds = currentDraw.state.linkFieldIds;
		if (linkFieldIds.indexOf(field.id) !== -1) {
			return FieldActionButton.viewLinkVerticalSeparation({
				linking: true,
				onclick: () => currentDraw.state.setTool(null),
			});
		}

		// No fields are being linked show the link start behavior
		return FieldActionButton.viewLinkVerticalSeparation({
			joinable: ctrl.getJoin(field) != null,
			hasTop: field.hasDrawableTop(),
			hasBottom: field.hasDrawableBottom(),
			hasError: inputState.hasError(),
			onclick: () => {
				CurrentState.cancelInteractions();

				const oppositeJoin = ctrl.getOppositeJoin(field);
				if (oppositeJoin != null) {
					currentDraw.state.setLinkFields([field.id, oppositeJoin.field.id]);
					NotificationService.info({ key: T.PHOTOMEASURE_JOIN_START, attrs: oppositeJoin.getTitle() });
					Services.Analytics.trackEvent(
						Services.Analytics.EventCategory.Mutate,
						'measurement:join',
						ctrl.collection.revision,
						1
					);
				} else {
					currentDraw.state.setLinkFields([field.id]);
				}
			},
		});
	},

	hasClear(field: IkeModel.CollectionModelFieldVerticalSeparation): boolean {
		return field.hasValue() || field.hasDrawableTop();
	},
};
